import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // send/receive session_token cookie
});

export async function createAudit(answers) {
  const { data } = await apiClient.post("/audits", answers);
  return data;
}

export async function getAudit(id) {
  const { data } = await apiClient.get(`/audits/${id}`);
  return data;
}

export async function listAudits() {
  // Cache-bust query so no proxy / browser ever serves a stale coach list.
  const { data } = await apiClient.get(`/audits?_=${Date.now()}`);
  return data;
}

// --- Coach: Client Accounts (Stage 2A) ---
export async function listCoachClients() {
  const { data } = await apiClient.get(`/coach/clients?_=${Date.now()}`);
  return data;
}

export async function getCoachClient(clientId) {
  const { data } = await apiClient.get(`/coach/clients/${clientId}?_=${Date.now()}`);
  return data;
}

// --- Coach: client admin actions (Stage 2B.1) ---
export async function setCoachClientStatus(clientId, status) {
  const { data } = await apiClient.patch(`/coach/clients/${clientId}/status`, { status });
  return data;
}

export async function sendCoachClientPasswordReset(clientId) {
  const { data } = await apiClient.post(`/coach/clients/${clientId}/password-reset`);
  return data;
}

export async function deleteCoachClient(clientId) {
  const { data } = await apiClient.delete(`/coach/clients/${clientId}`);
  return data;
}

export async function getVideoSetting() {
  const { data } = await apiClient.get("/settings/video");
  return data;
}

export async function setVideoSetting(url) {
  const { data } = await apiClient.put("/settings/video", { url });
  return data;
}

export async function uploadVideoFile(file, onProgress) {
  const form = new FormData();
  form.append("file", file, file.name);
  const { data } = await apiClient.post("/settings/video/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  });
  return data;
}

export async function clearVideoSetting() {
  const { data } = await apiClient.delete("/settings/video");
  return data;
}

export async function deleteAudit(id) {
  const { data } = await apiClient.delete(`/audits/${id}`);
  return data;
}

export async function uploadAuditPdf(id, blob) {
  const form = new FormData();
  form.append("file", blob, `roadmap-${id}.pdf`);
  const { data } = await apiClient.post(`/audits/${id}/pdf`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export function pdfDownloadUrl(id) {
  return `${API}/audits/${id}/pdf`;
}

// Auth
export async function authSession(sessionId) {
  const { data } = await apiClient.post("/auth/session", { session_id: sessionId });
  return data;
}

export async function authMe() {
  const { data } = await apiClient.get("/auth/me");
  return data;
}

export async function authLogout() {
  const { data } = await apiClient.post("/auth/logout");
  return data;
}

export async function getCoachClientCheckins(clientId, week) {
  const w = week ? `&week=${encodeURIComponent(week)}` : "";
  const { data } = await apiClient.get(`/coach/clients/${clientId}/checkins?_=${Date.now()}${w}`);
  return data;
}

export async function retryCoachInsights(clientId, submissionId) {
  const { data } = await apiClient.post(`/coach/clients/${clientId}/checkins/${submissionId}/insights/retry`);
  return data;
}

export async function getCoachCurrentRoadmap(clientId) {
  const { data } = await apiClient.get(`/coach/clients/${clientId}/roadmap/current?_=${Date.now()}`);
  return data;
}

export async function saveCoachRoadmapOverrides(clientId, fields) {
  const { data } = await apiClient.put(`/coach/clients/${clientId}/roadmap/overrides`, { fields });
  return data;
}

export async function getCoachNotes(clientId) {
  const { data } = await apiClient.get(`/coach/clients/${clientId}/notes?_=${Date.now()}`);
  return data;
}

export async function saveCoachNotes(clientId, notes) {
  const { data } = await apiClient.put(`/coach/clients/${clientId}/notes`, { notes });
  return data;
}

// Stage 2 — Weekly Check In Action Tracker, reminders, The Coaches Helper
export async function getTracker() {
  const { data } = await apiClient.get(`/coach/tracker?_=${Date.now()}`);
  return data;
}

export async function saveActionNotes(actionId, coaches_brief, action_taken) {
  const { data } = await apiClient.put(`/coach/tracker/actions/${actionId}/notes`, { coaches_brief, action_taken });
  return data;
}

export async function setActionStatus(actionId, status, coaches_brief, action_taken) {
  const { data } = await apiClient.post(`/coach/tracker/actions/${actionId}/status`, { status, coaches_brief, action_taken });
  return data;
}

export async function restoreAction(actionId) {
  const { data } = await apiClient.post(`/coach/tracker/actions/${actionId}/restore`);
  return data;
}

export async function saveActionReminder(actionId, text, due_date, due_time) {
  const { data } = await apiClient.put(`/coach/tracker/actions/${actionId}/reminder`, { text, due_date, due_time });
  return data;
}

export async function getDueReminders() {
  const { data } = await apiClient.get(`/coach/reminders/due?_=${Date.now()}`);
  return data;
}

export async function dismissReminder(reminderId) {
  const { data } = await apiClient.post(`/coach/reminders/${reminderId}/dismiss`);
  return data;
}

export async function askCoachesHelper(question, history) {
  const { data } = await apiClient.post(`/coach/helper/ask`, { question, history }, { timeout: 180000 });
  return data;
}
